// Review pane: the needs_review queue (GET /artifacts?status=needs_review,
// served from the local store). Each item is an editable ArtifactCard; the
// primary action confirms it (PUT status=confirmed). CONTRACTS §10.
//
// Tour-tag quick-reapply (DESIGN §9): binder pages upload roughly ordered by
// binder/tour, so neighbours usually share a tour. We hold the most-recently
// applied tour slug as session state HERE (pane level) so it persists as she
// moves between items, and surface it as the most prominent one-tap "reapply"
// affordance on the next card. `existingTourTags` (distinct slugs across the
// whole store) feeds the secondary one-tap reuse chips.

import { useState } from "react";
import type { Artifact } from "../types";
import { ArtifactCard } from "../components/ArtifactCard";

export interface ReviewPaneProps {
  items: Artifact[];
  /**
   * All `pending` artifacts (from the store). Ones that errored or have gone
   * stale are surfaced in a "Needs attention" section so a broken/stuck enrich
   * is visible and actionable here instead of vanishing (DESIGN §9).
   */
  pendingItems: Artifact[];
  loading: boolean;
  /** Distinct tour slugs across all loaded artifacts (one-tap reuse chips). */
  existingTourTags: string[];
  onChanged: () => void; // re-fetch from API after a mutation
  onUpsert: (a: Artifact) => void; // optimistic local update
  onRemove: (id: string) => void;
}

// A still-pending item this old almost certainly stalled (enrich is seconds).
const STALE_MS = 90_000;

export function ReviewPane({
  items,
  pendingItems,
  loading,
  existingTourTags,
  onChanged,
  onUpsert,
  onRemove,
}: ReviewPaneProps) {
  // Session-sticky most-recently-applied tour slug (persists across items).
  const [lastAppliedTour, setLastAppliedTour] = useState<string | null>(null);

  // Pending items that failed (enrichmentError) or stalled (no settle in time).
  const now = Date.now();
  const attention = pendingItems.filter(
    (a) => a.enrichmentError || now - Date.parse(a.updatedAt) > STALE_MS,
  );

  const renderCard = (a: Artifact, attentionItem = false) => (
    <ArtifactCard
      key={a.id}
      artifact={a}
      primaryLabel="Confirm"
      primaryStatus="confirmed"
      // Attention items (stuck/failed) get a re-run button so they're actionable.
      allowReenrich={attentionItem}
      existingTourTags={existingTourTags}
      lastAppliedTour={lastAppliedTour}
      onTourApplied={setLastAppliedTour}
      onSaved={onUpsert}
      onDeleted={onRemove}
      onReenriched={onChanged}
    />
  );

  const nothing = items.length === 0 && attention.length === 0;

  return (
    <div className="pane review-pane">
      {attention.length > 0 && (
        <section className="attention">
          <h2 className="attention__title">
            Needs attention <span className="attention__count">{attention.length}</span>
          </h2>
          <p className="attention__note">
            These never finished enriching. Re-run enrichment, take a better picture, fill them in
            by hand, or delete them.
          </p>
          <div className="card-grid">{attention.map((a) => renderCard(a, true))}</div>
        </section>
      )}

      {nothing ? (
        <p className="empty-state">
          {loading ? "Loading…" : "Nothing to review. Capture some pages in the Upload pane."}
        </p>
      ) : (
        items.length > 0 && (
          <div className="card-grid">{items.map((a) => renderCard(a))}</div>
        )
      )}
    </div>
  );
}
